import { useEffect, useRef, useCallback } from 'react';
import { trackEvent } from 'utils/analytics';

const SESSION_KEY_PREFIX = 'ph_impression_';

const getSessionStorageKey = (sectionName: string, eventName?: string) => {
  return `${SESSION_KEY_PREFIX}${eventName ?? sectionName}`;
};

const hasTrackedInSession = (sectionName: string, eventName?: string): boolean => {
  try {
    return sessionStorage.getItem(getSessionStorageKey(sectionName, eventName)) === '1';
  } catch {
    return false;
  }
};

const markTrackedInSession = (sectionName: string, eventName?: string): void => {
  try {
    sessionStorage.setItem(getSessionStorageKey(sectionName, eventName), '1');
  } catch {
    // sessionStorage may be unavailable
  }
};

interface ImpressionTrackingOptions {
  sectionName: string;
  eventName?: string;
  threshold?: number;
  trackOnce?: boolean;
  additionalData?: Record<string, unknown>;
}

const useImpressionTracking = ({
  sectionName,
  eventName,
  threshold = 0.5,
  trackOnce = true,
  additionalData = {},
}: ImpressionTrackingOptions) => {
  const ref = useRef<HTMLElement>(null);
  const hasTracked = useRef(false);

  const trackImpression = useCallback(() => {
    if (trackOnce && hasTracked.current) return;
    if (trackOnce && hasTrackedInSession(sectionName, eventName)) {
      hasTracked.current = true;
      return;
    }

    trackEvent(eventName ?? `viewed_section_${sectionName}`, {
      section: sectionName,
      timestamp: new Date().toISOString(),
      ...additionalData,
    });

    hasTracked.current = true;
    if (trackOnce) {
      markTrackedInSession(sectionName, eventName);
    }
  }, [additionalData, eventName, sectionName, trackOnce]);

  useEffect(() => {
    const element = ref.current;
    if (!element || typeof IntersectionObserver === 'undefined') return;

    if (trackOnce && hasTrackedInSession(sectionName, eventName)) {
      hasTracked.current = true;
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            trackImpression();
            if (trackOnce) {
              observer.unobserve(element);
            }
          }
        });
      },
      { threshold },
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [eventName, trackImpression, threshold, trackOnce, sectionName]);

  return ref;
};

export default useImpressionTracking;
